<template>
  <div :class="compact ? '' : 'rb-date-card'">
    <label v-if="!compact" class="rb-label">{{ $t('ReportBuilder.dateRange.reportPeriod') }}</label>
    <el-select v-model="dateRangeTypeStr" :placeholder="$t('ReportBuilder.dateRange.selectPeriod')" :style="{ width: compact ? '160px' : '100%' }">
      <el-option :label="$t('ReportBuilder.dateRange.today')" value="today" />
      <el-option :label="$t('ReportBuilder.dateRange.yesterday')" value="yesterday" />
      <el-option :label="$t('ReportBuilder.dateRange.last2days')" value="-2d" />
      <el-option :label="$t('ReportBuilder.dateRange.last7days')" value="-7d" />
      <el-option :label="$t('ReportBuilder.dateRange.last30days')" value="-30d" />
      <el-option :label="$t('ReportBuilder.dateRange.last90days')" value="-90d" />
      <el-option :label="$t('ReportBuilder.dateRange.customDays')" value="custom" />
      <el-option :label="$t('ReportBuilder.dateRange.exactDates')" value="explicit" />
    </el-select>

    <el-select
      v-if="isTrends"
      v-model="granularity"
      :placeholder="$t('ReportBuilder.constructor.granularityLabel')"
      :style="{ width: compact ? '120px' : '100%', marginLeft: compact ? '8px' : '0', marginTop: compact ? '0' : '12px' }"
    >
      <el-option :label="$t('ReportBuilder.constructor.granularity.minute')" value="minute" :disabled="diffDays > 2" />
      <el-option :label="$t('ReportBuilder.constructor.granularity.hour')" value="hour" :disabled="diffDays > 2" />
      <el-option :label="$t('ReportBuilder.constructor.granularity.day')" value="day" />
      <el-option :label="$t('ReportBuilder.constructor.granularity.week')" value="week" />
      <el-option :label="$t('ReportBuilder.constructor.granularity.month')" value="month" />
    </el-select>

    <div v-if="dateRangeTypeStr === 'custom'" class="mt-3">
      <el-input v-model.number="customDays" type="number" min="1" :placeholder="$t('ReportBuilder.dateRange.numberOfDays')" @input="onCustomDaysChange">
        <template #append>{{ $t('ReportBuilder.dateRange.daysAgo') }}</template>
      </el-input>
    </div>

    <div v-if="dateRangeTypeStr === 'explicit'" class="mt-3">
      <el-date-picker
        v-model="explicitDateRange"
        type="datetimerange"
        :range-separator="$t('ReportBuilder.dateRange.to')"
        :start-placeholder="$t('ReportBuilder.dateRange.startDate')"
        :end-placeholder="$t('ReportBuilder.dateRange.endDate')"
        value-format="YYYY-MM-DD HH:mm:ss"
        :default-time="[new Date(2000, 1, 1, 0, 0, 0), new Date(2000, 1, 1, 23, 59, 59)]"
        style="width: 100%"
        @change="onExplicitDatesChange"
      />
    </div>
  </div>
</template>

<script>
import { classifyDateRangeType } from "@/helpers/dateRangeType";

export default {
  name: "_dateRange",
  props: {
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      customDays: 14,
      explicitDateRange: []
    }
  },
  computed: {
    dateFrom: {
      get() {
        return this.$store.state.ReportFromBuilder.settings.params[':date_from'] || '-30d'
      },
      set(val) {
        this.$store.commit('ReportFromBuilder/setSettingParam', { name: ':date_from', value: val })
      }
    },
    dateTo: {
      get() {
        return this.$store.state.ReportFromBuilder.settings.params[':date_to'] || 'now'
      },
      set(val) {
        this.$store.commit('ReportFromBuilder/setSettingParam', { name: ':date_to', value: val })
      }
    },
    dateRangeTypeStr: {
      get() {
        return classifyDateRangeType(this.dateFrom, this.dateTo);
      },
      set(val) {
        this.onTypeChange(val);
      }
    },
    hardcodedDateOnLoad() {
      return this.$store.state.ReportFromBuilder.hardcodedDateOnLoad;
    },
    isTrends() {
      return this.$store.state.ReportFromBuilder.type === 'trends';
    },
    granularity: {
      get() {
        return this.$store.state.ReportFromBuilder.source_request.granularity || 'day'
      },
      set(val) {
        this.$store.commit('ReportFromBuilder/setGranularity', val)
      }
    },
    diffDays() {
      const fromStr = this.dateFrom;
      const toStr = this.dateTo;
      let diff = 30; // default safe value
      
      if (fromStr === 'today' || fromStr === '-24h') {
        diff = 1;
      } else if (fromStr && String(fromStr).startsWith('-') && String(fromStr).endsWith('d')) {
        diff = parseInt(String(fromStr).replace('-', '').replace('d', ''));
      } else if (fromStr && toStr && String(fromStr).includes('-')) {
        const start = new Date(String(fromStr).replace(/-/g, '/'));
        let end = new Date();
        if (toStr !== 'now') {
          end = new Date(String(toStr).replace(/-/g, '/'));
        }
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
        }
      }
      return diff;
    }
  },
  watch: {
    dateFrom: {
      immediate: true,
      handler(newVal) {
        if (this.dateRangeTypeStr === 'custom' && newVal && String(newVal).startsWith('-') && String(newVal).endsWith('d')) {
          const days = parseInt(newVal.replace('-', '').replace('d', ''));
          if (!isNaN(days)) {
            this.customDays = days;
          }
        } else if (this.dateRangeTypeStr === 'explicit') {
          this.explicitDateRange = [this.dateFrom, this.dateTo];
        }
      }
    },
    diffDays: {
      immediate: true,
      handler(newVal) {
        if (newVal > 2 && (this.granularity === 'hour' || this.granularity === 'minute')) {
          this.granularity = 'day';
        } else if (newVal <= 2 && this.granularity !== 'minute' && this.granularity !== 'hour') {
          this.granularity = 'hour';
        }
      }
    }
  },
  methods: {
    onTypeChange(val) {
      // User is now deliberately choosing a period, so the "this report was opened with
      // hardcoded dates" warning no longer applies for the rest of this session.
      this.$store.commit('ReportFromBuilder/clearHardcodedDateWarning');
      if (val === 'today') {
        this.dateFrom = 'today';
        this.dateTo = 'now';
      } else if (val === 'yesterday') {
        const fmt = (d) => {
            return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0');
        }
        const to = new Date(); to.setDate(to.getDate() - 1); to.setHours(23, 59, 59, 999);
        const from = new Date(); from.setDate(from.getDate() - 1); from.setHours(0, 0, 0, 0);
        this.dateFrom = fmt(from);
        this.dateTo = fmt(to);
      } else if (val === '-2d' || val === '-7d' || val === '-30d' || val === '-90d') {
        this.dateFrom = val;
        this.dateTo = 'now';
      } else if (val === 'custom') {
        this.dateFrom = `-${this.customDays}d`;
        this.dateTo = 'now';
      } else if (val === 'explicit') {
        if (!this.explicitDateRange || this.explicitDateRange.length !== 2) {
          const to = new Date()
          const from = new Date()
          from.setDate(from.getDate() - 30)
          
          const format = (d) => {
              return d.getFullYear() + '-' +
                  String(d.getMonth() + 1).padStart(2, '0') + '-' +
                  String(d.getDate()).padStart(2, '0') + ' ' +
                  String(d.getHours()).padStart(2, '0') + ':' +
                  String(d.getMinutes()).padStart(2, '0') + ':' +
                  String(d.getSeconds()).padStart(2, '0')
          }
          this.explicitDateRange = [format(from), format(to)];
        }
        this.dateFrom = this.explicitDateRange[0]
        this.dateTo = this.explicitDateRange[1]
      }
    },
    onCustomDaysChange(val) {
      if (val && val > 0) {
        this.dateFrom = `-${val}d`;
        this.dateTo = 'now';
      }
    },
    onExplicitDatesChange(val) {
      if (val && val.length === 2) {
        this.dateFrom = val[0];
        this.dateTo = val[1];
      }
    }
  }
}
</script>

<style scoped>
.rb-date-card {
  background: var(--ui-bg-card, #fff);
  border: 1px solid var(--ui-border, #e8e8e8);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 12px;
}

.rb-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ui-text-2, #606266);
  margin-bottom: 6px;
}
</style>
